﻿import { addDays, addMinutes, format as dateFnsFormat, isBefore, startOfToday, subDays } from "date-fns";

export const todayInSerializedFormat = () => {
    return toIsoFormatWithoutTime(startOfToday());
};
export const twoWeeksFromTodayInSerializedFormat = () => {
    const today = startOfToday();
    const twoWeeks = addDays(today, 14);
    return toIsoFormatWithoutTime(twoWeeks);
};
export const toIsoFormat = (date: Date) => {
    return date.toISOString();
};
export const toIsoFormatWithoutTime = (date: Date) => {
    return dropTimeFromIsoFormat(date.toISOString());
};
export const dropTimeFromIsoFormat = <T extends string | null | undefined>(date: T): T => {
    if (typeof date === "string") {
        const indexOfT = date.indexOf("T");
        if (indexOfT >= 0) {
            // @ts-ignore
            return date.substring(0, indexOfT);
        }
    }
    return date;
};
export const isDatePast = (dateStr: string | null | undefined): boolean => {
    if (!dateStr) {
        return false;
    }
    if (dateStr.indexOf("T") >= 0) {
        throw Error("Invalid date format. A date should not have time and time zone information.");
    }

    const beginningOfDayInUTC = new Date(dateStr); // without time zone information, it will assume a UTC time zone
    const beginningOfDayInLocalTimeZone = addMinutes(beginningOfDayInUTC, beginningOfDayInUTC.getTimezoneOffset()); // convert to local time zone
    const today = startOfToday();
    return isBefore(beginningOfDayInLocalTimeZone, today);
};
export const add1Day = <T extends string | null | undefined>(dateStr: T): T => {
    if (!dateStr) {
        return dateStr;
    }
    if (dateStr.indexOf("T") >= 0) {
        throw Error("Invalid date format. A date should not have time and time zone information.");
    }

    const beginningOfDayInUTC = new Date(dateStr); // without time zone information, it will assume a UTC time zone
    const beginningOfDayInLocalTimeZone = addMinutes(beginningOfDayInUTC, beginningOfDayInUTC.getTimezoneOffset()); // convert to local time zone
    // @ts-ignore
    return toIsoFormatWithoutTime(addDays(beginningOfDayInLocalTimeZone, 1));
};
const formatDateHelper = (timestamp: Date | null | undefined, dateFormat = "P", replaceToday = true, appendTime?: boolean, timeFormat?: string) => {
    if (!timestamp) {
        return "";
    }

    const date = timestamp;
    const today = startOfToday();
    const yesterday = subDays(today, 1);
    const tomorrow = addDays(today, 1);
    const dayAfterTomorrow = addDays(today, 2);

    let result;
    if (replaceToday && !isBefore(date, yesterday) && isBefore(date, today)) {
        result = `Yesterday`;
    } else if (replaceToday && !isBefore(date, tomorrow) && isBefore(date, dayAfterTomorrow)) {
        result = `Tomorrow`;
    } else if (replaceToday && !isBefore(date, today) && isBefore(date, tomorrow)) {
        result = `Today`;
    } else {
        result = dateFnsFormat(date, dateFormat);
    }

    if (appendTime && timeFormat) {
        result += ` ${dateFnsFormat(date, timeFormat)}`;
    }

    return result;
};

export const formatDate = (timestamp: Date | null | undefined, dateFormat = "P", replaceToday = true, appendTime = false, timeFormat?: string) => {
    return formatDateHelper(timestamp, dateFormat, replaceToday, appendTime, timeFormat);
};

export const formatTimestamp = (timestamp: Date | null | undefined, dateFormat = "P", replaceToday = true, appendTime = true, timeFormat = "p") => {
    return formatDateHelper(timestamp, dateFormat, replaceToday, appendTime, timeFormat);
};

export const formatTimestampStr = (timestampStr: string | null | undefined, dateFormat = "P", replaceToday = true, appendTime = true, timeFormat = "p") => {
    if (!timestampStr) {
        return "";
    }
    if (timestampStr.indexOf("T") < 0) {
        throw Error("Invalid timestamp format. A timestamp should have time and time zone information.");
    }
    return formatDateHelper(new Date(timestampStr), dateFormat, replaceToday, appendTime, timeFormat);
};

export const formatDateStr = (dateStr?: string | null | undefined, dateFormat = "P", replaceToday = true, appendTime = false, timeFormat?: string) => {
    if (!dateStr) {
        return "";
    }
    if (dateStr.indexOf("T") >= 0) {
        throw Error("Invalid date format. A date should not have time and time zone information.");
    }

    const beginningOfDayInUTC = new Date(dateStr); // without time zone information, it will assume a UTC time zone
    const beginningOfDayInLocalTimeZone = addMinutes(beginningOfDayInUTC, beginningOfDayInUTC.getTimezoneOffset()); // convert to local time zone
    return formatDateHelper(beginningOfDayInLocalTimeZone, dateFormat, replaceToday, appendTime, timeFormat);
};
